// @section 1 AccountDeliveryLens — Delivery + Offers
//
// Delivery surface inside AccountWorkspace. Two stacked sections:
//
//   Offers       — deal-scoped versioned scopes/proposals with line items.
//                  Absorbed from the old BD lens. The BD lens still owns the
//                  Ask → "+ Add to offer" workflow (which appends a line item);
//                  this lens is where the offer itself is viewed, drafted
//                  manually, and (in a follow-up) sent.
//   Engagements  — top-level client engagements → workstreams → deliverables.
//                  Engagements lazy-expand to load their workstreams +
//                  deliverables; inline create at each level; share-link
//                  generation per deliverable.
//
// The lifecycle this lens surfaces: deal → offer → engagement → workstreams →
// deliverables. Top to bottom.

import type { CSSProperties } from 'react';
import { useEffect, useState } from 'react';
import {
  listAccountEngagements, createEngagement,
  listWorkstreams, createWorkstream,
  listAccountDeliverables, createDeliverable,
  createShareLink,
  listBdOffers, createBdOffer, listBdOfferItems,
  type AccountAggregate, type AccountDeal,
  type Engagement, type Workstream, type Deliverable,
  type BdOffer, type BdOfferItem,
} from '../../api';

export default function AccountDeliveryLens({ data }: { data: AccountAggregate }) {
  const companyId = data.company.id;
  const [engagements, setEngagements] = useState<Engagement[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const reload = () => listAccountEngagements(companyId).then(setEngagements).catch((e) => setErr(String(e)));
  useEffect(() => { reload(); }, [companyId]);

  return (
    <div style={{ display: 'grid', gap: 18 }}>
      {/* @block 1.1 Offers — absorbed from the old BD lens */}
      <OffersSection companyId={companyId} deals={data.deals} />

      {/* @block 1.2 Engagements → workstreams → deliverables */}
      <Card title="Engagements" subtitle={engagements ? `${engagements.length} active` : 'Loading…'} err={err}>
        {engagements !== null && (
          <>
            {engagements.length === 0 ? (
              <Empty>No engagements on this account. Add one below to start tracking deliverables.</Empty>
            ) : (
              <div style={{ display: 'grid', gap: 8 }}>
                {engagements.map((e) => <EngagementBlock key={e.id} engagement={e} />)}
              </div>
            )}
            <EngagementForm companyId={companyId} deals={data.deals} onCreated={reload} />
          </>
        )}
      </Card>
    </div>
  );
}

// @section 2 Offers — deal-scoped versioned scopes / proposals with line items
function OffersSection({ companyId, deals }: { companyId: string; deals: AccountDeal[] }) {
  const [items, setItems] = useState<BdOffer[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const reload = () => listBdOffers({ companyId }).then(setItems).catch((e) => setErr(String(e)));
  useEffect(() => { reload(); }, [companyId]);

  const dealName = (id: string) => deals.find((d) => d.id === id)?.name ?? id.slice(0, 8);

  return (
    <Card
      title="Offers"
      subtitle={items ? `${items.length} versioned · send workflow lands in a follow-up` : 'Loading…'}
      err={err}
    >
      {items !== null && (
        <>
          {items.length === 0 ? (
            <Empty>No offers drafted yet. Add a line item from an open Ask in the BD lens to start an offer automatically, or draft one manually below.</Empty>
          ) : (
            <div style={{ display: 'grid', gap: 10 }}>
              {items.map((o) => (
                <OfferCard key={o.id} offer={o} dealName={dealName(o.dealId)} />
              ))}
            </div>
          )}
          {deals.length > 0 && <OfferForm deals={deals} onCreated={reload} />}
        </>
      )}
    </Card>
  );
}

// @block 2.1 OfferCard — per-offer card showing header + line items inline.
function OfferCard({ offer, dealName }: { offer: BdOffer; dealName: string }) {
  const [items, setItems] = useState<BdOfferItem[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    listBdOfferItems(offer.id).then(setItems).catch((e) => setErr(String(e)));
  }, [offer.id]);

  const itemsTotalCents = (items ?? []).reduce((s, it) => s + it.lineTotalCents, 0);
  const headerTotal = offer.totalCents ?? itemsTotalCents;

  return (
    <div style={{ border: '1px solid #232a37', borderRadius: 6, padding: 10, background: '#1d2330' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
        <div style={{ fontSize: 13 }}>
          <strong>{dealName}</strong>
          <span style={{ marginLeft: 8, color: '#a3b1c2' }}>v{offer.version}</span>
          <OfferStatusBadge status={offer.status} />
        </div>
        <div style={{ fontSize: 13, fontVariantNumeric: 'tabular-nums' }}>
          {fmtMoney(headerTotal, offer.currency)}
        </div>
      </div>
      {offer.summary && <div style={{ fontSize: 12, color: '#a3b1c2', marginTop: 4 }}>{offer.summary}</div>}
      {err && <div style={{ color: '#e87e7e', fontSize: 11, marginTop: 4 }}>{err}</div>}
      {items === null ? (
        <div style={{ fontSize: 11, color: '#6b7a90', marginTop: 6 }}>Loading items…</div>
      ) : items.length === 0 ? (
        <div style={{ fontSize: 11, color: '#6b7a90', marginTop: 6, fontStyle: 'italic' }}>
          No line items yet. Click "→ Add to offer" on any open Ask in the BD lens to append one.
        </div>
      ) : (
        <ul style={{ listStyle: 'none', padding: 0, margin: '8px 0 0' }}>
          {items.map((it) => (
            <li key={it.id} style={{
              fontSize: 12, padding: '4px 0', borderTop: '1px solid #232a37',
              display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8,
            }}>
              <div style={{ minWidth: 0, flex: 1 }}>
                <span>{it.name}</span>
                {it.addressesAskId && (
                  <span style={{
                    fontSize: 9, marginLeft: 8, padding: '1px 6px', borderRadius: 3,
                    background: '#7eb6e822', color: '#7eb6e8', textTransform: 'uppercase', letterSpacing: 1,
                  }}>
                    addresses ask
                  </span>
                )}
                {it.recurring && it.recurring !== 'one-time' && (
                  <span style={{ marginLeft: 6, fontSize: 10, color: '#a08fd8' }}>· {it.recurring}</span>
                )}
              </div>
              <div style={{ fontSize: 12, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', color: '#a3b1c2' }}>
                {it.qty} × {fmtMoney(it.unitCents)} = <strong>{fmtMoney(it.lineTotalCents)}</strong>
              </div>
            </li>
          ))}
        </ul>
      )}
      <div style={{ fontSize: 10, color: '#6b7a90', marginTop: 6 }}>
        {offer.sentAt ? `sent ${new Date(offer.sentAt).toLocaleDateString()}` : 'draft'}
        {' · '}updated {new Date(offer.updatedAt).toLocaleDateString()}
      </div>
    </div>
  );
}

function OfferForm({ deals, onCreated }: { deals: AccountDeal[]; onCreated: () => void }) {
  const [dealId, setDealId] = useState(deals[0].id);
  const [summary, setSummary] = useState('');
  const [totalDollars, setTotalDollars] = useState('');
  const [busy, setBusy] = useState(false);

  return (
    <form
      style={formStyle}
      onSubmit={async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
          const totalCents = totalDollars ? Math.round(parseFloat(totalDollars) * 100) : undefined;
          await createBdOffer({ dealId, summary: summary.trim() || undefined, totalCents });
          setSummary(''); setTotalDollars('');
          onCreated();
        } finally { setBusy(false); }
      }}
    >
      <DealPicker deals={deals} value={dealId} onChange={setDealId} />
      <input placeholder="summary (optional)" value={summary} onChange={(e) => setSummary(e.target.value)} style={{ flex: 1 }} />
      <input
        type="number"
        step="0.01"
        placeholder="total ($)"
        value={totalDollars}
        onChange={(e) => setTotalDollars(e.target.value)}
        style={{ width: 110 }}
      />
      <button type="submit" disabled={busy}>Draft new offer</button>
    </form>
  );
}

function DealPicker({ deals, value, onChange }: { deals: AccountDeal[]; value: string; onChange: (v: string) => void }) {
  if (deals.length === 1) {
    return <span style={{ fontSize: 11, color: '#6b7a90' }}>deal: {deals[0].name}</span>;
  }
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)}>
      {deals.map((d) => (
        <option key={d.id} value={d.id}>{d.name}{d.closedAt ? ' (closed)' : ''}</option>
      ))}
    </select>
  );
}

function OfferStatusBadge({ status }: { status: string }) {
  const tone = status === 'draft' ? '#a3b1c2'
    : status === 'sent' ? '#7eb6e8'
    : status === 'accepted' ? '#6ec78a'
    : status === 'rejected' || status === 'superseded' ? '#6b7a90'
    : '#a3b1c2';
  return (
    <span style={{
      fontSize: 9, textTransform: 'uppercase', letterSpacing: 1, fontWeight: 600,
      padding: '2px 6px', borderRadius: 3, marginLeft: 8,
      background: `${tone}22`, color: tone,
    }}>{status}</span>
  );
}

// @section 3 Engagement block — collapsible header with workstreams nested inside
function EngagementBlock({ engagement }: { engagement: Engagement }) {
  const [open, setOpen] = useState(false);
  const [workstreams, setWorkstreams] = useState<Workstream[] | null>(null);
  const [deliverables, setDeliverables] = useState<Deliverable[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const reload = () => Promise.all([
    listWorkstreams(engagement.id),
    listAccountDeliverables({ engagementId: engagement.id }),
  ]).then(([ws, dl]) => { setWorkstreams(ws); setDeliverables(dl); })
    .catch((e) => setErr(String(e)));

  useEffect(() => {
    if (open && workstreams === null) reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <div style={engagementBlockStyle}>
      <div
        onClick={() => setOpen(!open)}
        style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 10px' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 12, color: '#6b7a90' }}>{open ? '▾' : '▸'}</span>
          <strong style={{ fontSize: 14 }}>{engagement.name}</strong>
          <StatusBadge status={engagement.status} />
        </div>
        <span style={{ fontSize: 11, color: '#6b7a90' }}>created {new Date(engagement.createdAt).toLocaleDateString()}</span>
      </div>
      {open && (
        <div style={{ padding: '4px 10px 10px', borderTop: '1px solid #232a37' }}>
          {err && <div style={{ color: '#e87e7e', fontSize: 12 }}>{err}</div>}
          {workstreams === null ? (
            <div style={{ fontSize: 12, color: '#6b7a90' }}>Loading workstreams…</div>
          ) : workstreams.length === 0 ? (
            <Empty>No workstreams yet. Add one below.</Empty>
          ) : (
            <div style={{ display: 'grid', gap: 6 }}>
              {workstreams.map((w) => (
                <WorkstreamBlock
                  key={w.id}
                  workstream={w}
                  deliverables={(deliverables ?? []).filter((d) => d.workstreamId === w.id)}
                  onChanged={reload}
                />
              ))}
            </div>
          )}
          <WorkstreamForm engagementId={engagement.id} onCreated={reload} />
        </div>
      )}
    </div>
  );
}

function EngagementForm({ companyId, deals, onCreated }: {
  companyId: string;
  deals: AccountAggregate['deals'];
  onCreated: () => void;
}) {
  const [name, setName] = useState('');
  const [dealId, setDealId] = useState(deals[0]?.id ?? '');
  const [summary, setSummary] = useState('');
  const [busy, setBusy] = useState(false);

  return (
    <form
      style={formStyle}
      onSubmit={async (e) => {
        e.preventDefault();
        if (!name.trim()) return;
        setBusy(true);
        try {
          await createEngagement({
            name: name.trim(),
            companyId,
            dealId: dealId || undefined,
            summary: summary.trim() || undefined,
          });
          setName(''); setSummary('');
          onCreated();
        } finally { setBusy(false); }
      }}
    >
      <input placeholder="Engagement name" value={name} onChange={(e) => setName(e.target.value)} style={{ flex: 1 }} />
      {deals.length > 0 && (
        <select value={dealId} onChange={(e) => setDealId(e.target.value)}>
          <option value="">(no deal link)</option>
          {deals.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
        </select>
      )}
      <input placeholder="summary (optional)" value={summary} onChange={(e) => setSummary(e.target.value)} style={{ flex: 1 }} />
      <button type="submit" disabled={busy || !name.trim()}>Add engagement</button>
    </form>
  );
}

// @section 4 Workstream block — deliverables nested inside
function WorkstreamBlock({ workstream, deliverables, onChanged }: {
  workstream: Workstream;
  deliverables: Deliverable[];
  onChanged: () => void;
}) {
  return (
    <div style={workstreamBlockStyle}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
        <div>
          <span style={{ fontSize: 13, fontWeight: 500 }}>{workstream.name}</span>
          <StatusBadge status={workstream.status} />
        </div>
        <span style={{ fontSize: 11, color: '#6b7a90' }}>{deliverables.length} deliverable{deliverables.length === 1 ? '' : 's'}</span>
      </div>
      {deliverables.length > 0 && (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {deliverables.map((d) => <DeliverableRow key={d.id} deliverable={d} />)}
        </ul>
      )}
      <DeliverableForm workstreamId={workstream.id} onCreated={onChanged} />
    </div>
  );
}

function WorkstreamForm({ engagementId, onCreated }: { engagementId: string; onCreated: () => void }) {
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  return (
    <form
      style={{ ...formStyle, marginTop: 8 }}
      onSubmit={async (e) => {
        e.preventDefault();
        if (!name.trim()) return;
        setBusy(true);
        try {
          await createWorkstream({ engagementId, name: name.trim() });
          setName('');
          onCreated();
        } finally { setBusy(false); }
      }}
    >
      <input placeholder="+ Workstream name" value={name} onChange={(e) => setName(e.target.value)} style={{ flex: 1 }} />
      <button type="submit" disabled={busy || !name.trim()}>Add</button>
    </form>
  );
}

// @section 5 Deliverable row + form
function DeliverableRow({ deliverable }: { deliverable: Deliverable }) {
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function makeShare() {
    setBusy(true);
    try {
      const link = await createShareLink(deliverable.id);
      setShareUrl(link.url);
      try { await navigator.clipboard.writeText(link.url); } catch { /* ignore clipboard denied */ }
    } finally { setBusy(false); }
  }

  return (
    <li style={{ padding: '4px 0', borderTop: '1px solid #232a37', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13 }}>
          {deliverable.title}
          <span style={{ fontSize: 10, color: '#6b7a90', marginLeft: 6 }}>v{deliverable.fileVersion}</span>
        </div>
        <div style={{ fontSize: 11, color: '#6b7a90', marginTop: 1 }}>
          <StatusBadge status={deliverable.status} />
          {deliverable.dueDate && <> · due {deliverable.dueDate}</>}
          {deliverable.driveUrl && <> · <a href={deliverable.driveUrl} target="_blank" rel="noreferrer">drive</a></>}
        </div>
      </div>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0 }}>
        {shareUrl ? (
          <span style={{ fontSize: 11, color: '#6ec78a' }}>link copied ✓</span>
        ) : (
          <button onClick={makeShare} disabled={busy} style={btnSm}>Share link</button>
        )}
      </div>
    </li>
  );
}

function DeliverableForm({ workstreamId, onCreated }: { workstreamId: string; onCreated: () => void }) {
  const [title, setTitle] = useState('');
  const [driveUrl, setDriveUrl] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [busy, setBusy] = useState(false);
  return (
    <form
      style={{ ...formStyle, marginTop: 6, paddingTop: 6, borderTop: '1px dashed #1d2330' }}
      onSubmit={async (e) => {
        e.preventDefault();
        if (!title.trim()) return;
        setBusy(true);
        try {
          await createDeliverable({
            workstreamId,
            title: title.trim(),
            driveUrl: driveUrl.trim() || undefined,
            dueDate: dueDate || undefined,
          });
          setTitle(''); setDriveUrl(''); setDueDate('');
          onCreated();
        } finally { setBusy(false); }
      }}
    >
      <input placeholder="+ Deliverable title" value={title} onChange={(e) => setTitle(e.target.value)} style={{ flex: 1 }} />
      <input placeholder="drive URL (optional)" value={driveUrl} onChange={(e) => setDriveUrl(e.target.value)} style={{ flex: 1 }} />
      <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} style={{ width: 140 }} />
      <button type="submit" disabled={busy || !title.trim()}>Add</button>
    </form>
  );
}

// @section 6 Shared
function Card({ title, subtitle, err, children }: { title: string; subtitle?: string; err?: string | null; children: React.ReactNode }) {
  return (
    <section style={cardStyle}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 10 }}>
        <h3 style={{ margin: 0, fontSize: 14, textTransform: 'uppercase', letterSpacing: 1.5, color: '#a3b1c2' }}>{title}</h3>
        {subtitle && <span style={{ fontSize: 11, color: '#6b7a90' }}>{subtitle}</span>}
      </div>
      {err && <div style={{ color: '#e87e7e', fontSize: 12, marginBottom: 8 }}>{err}</div>}
      {children}
    </section>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return <em style={{ color: '#6b7a90', fontSize: 13 }}>{children}</em>;
}

function StatusBadge({ status }: { status: string }) {
  const tone = status === 'active' ? '#6ec78a'
    : status === 'paused' ? '#f0c674'
    : status === 'done' || status === 'approved' ? '#6ec78a'
    : status === 'rejected' ? '#e87e7e'
    : status === 'in_review' || status === 'client_review' ? '#7eb6e8'
    : status === 'draft' ? '#a3b1c2'
    : '#6b7a90';
  return (
    <span style={{
      fontSize: 9, textTransform: 'uppercase', letterSpacing: 1, fontWeight: 600,
      padding: '2px 6px', borderRadius: 3, marginLeft: 8,
      background: `${tone}22`, color: tone,
    }}>{status}</span>
  );
}

function fmtMoney(cents?: number | null, currency: string = 'USD'): string {
  if (cents == null) return '—';
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency, maximumFractionDigits: 0 }).format(cents / 100);
  } catch {
    return `$${(cents / 100).toLocaleString()}`;
  }
}

const cardStyle: CSSProperties = {
  border: '1px solid #232a37',
  borderRadius: 8,
  padding: 14,
  background: '#171c26',
};
const engagementBlockStyle: CSSProperties = {
  border: '1px solid #232a37',
  borderRadius: 6,
  background: '#1d2330',
};
const workstreamBlockStyle: CSSProperties = {
  padding: '8px 10px',
  borderRadius: 4,
  background: '#171c26',
  border: '1px solid #232a37',
};
const formStyle: CSSProperties = {
  marginTop: 12,
  paddingTop: 12,
  borderTop: '1px dashed #232a37',
  display: 'flex',
  gap: 8,
  alignItems: 'center',
  flexWrap: 'wrap',
};
const btnSm: CSSProperties = {
  fontSize: 11,
  padding: '3px 8px',
  border: '1px solid #2a3346',
  borderRadius: 4,
  background: 'transparent',
  color: '#a3b1c2',
  cursor: 'pointer',
};
